import React from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import GenericModal from '../../../../components/GenericModal'
import { DivisionManager } from '../../../../APIManager'
import { notification } from '../../../../components/Notifications'
import { useReportRegenerationTask } from '../../../../hooks/useReportRegenerationTask'
import { ReportActionModal } from '../ReportActionModal'
import { type ReportActionKind, REPORT_ACTION_COPY, formatReportScope, buildStartedSubtitle } from '../reportActionCopy'
import { HttpStatusCode } from '../../../../consts/httpStatusCode'

interface ReportMutationActionProps {
  isOpen: boolean
  onClose: () => void
  // Reload the report once the task completes (the ReportsControls `fetchReports`).
  onCompleted: () => void
  // Which report action to run — drives the endpoint, copy, and toasts.
  action: ReportActionKind
  // Display name of the report target being viewed (for the modal copy).
  targetName?: string
  // Survey project + term the action targets (for unambiguous modal/toast copy).
  surveyProjectTitle?: string
  termName?: string
  // True when the current report scope is a Course Section (vs an Academic Unit).
  isSection?: boolean
  // Version to revert to: omit for 'revert-previous'; the latest version for 'restore-latest'.
  // Ignored for 'regenerate'.
  targetVersion?: number
}

// Controlled modal that owns the regenerate / revert / restore request + task polling. All
// three share the 202 `{ task_id }` contract, so they reuse one polling hook. Visibility of
// the menu entry that opens it is gated by the caller.
const ReportMutationAction: React.FC<ReportMutationActionProps> = ({
  isOpen,
  onClose,
  onCompleted,
  action,
  targetName,
  surveyProjectTitle,
  termName,
  isSection = false,
  targetVersion
}) => {
  const { id: divisionId } = useParams()
  const [searchParams] = useSearchParams()
  const termId = searchParams.get('termId')
  const distributionSeriesId = searchParams.get('projectId')
  const copy = REPORT_ACTION_COPY[action]
  const isRevert = action !== 'regenerate'

  const { start: startPolling } = useReportRegenerationTask({
    onSuccess: () => {
      notification('Report updated', { variant: 'success', subTitle: copy.doneSubtitle })
      onCompleted()
    },
    onError: () => {
      notification(copy.failedTitle, { variant: 'error', subTitle: 'Please try again.' })
    }
  })

  const { mutate, isPending } = useMutation({
    mutationFn: async (includeChildren: boolean) => {
      const params = { termId, distributionSeriesId }
      if (isRevert) {
        return await DivisionManager.revertReport(divisionId ?? '', params, {
          include_children: includeChildren,
          ...(targetVersion != null ? { target_version: targetVersion } : {})
        })
      }
      return await DivisionManager.regenerateReport(divisionId ?? '', params, { include_children: includeChildren })
    },
    onSuccess: (response) => {
      // The shared `api` client's axios-case-converter middleware camelizes every response key
      // (src/utils/api.ts) — the backend's task_id/survey_project_title/term_name always arrive
      // as taskId/surveyProjectTitle/termName. Reading the snake_case names here always read
      // undefined, so polling silently never started and the toast never named the target.
      const taskId = response?.data?.taskId
      if (taskId) startPolling(taskId)
      // Prefer the backend's echoed target/project names so the toast names exactly what is
      // changing; fall back to the message, then a generic line.
      const echoed = formatReportScope(response?.data?.surveyProjectTitle, response?.data?.termName)
      notification(copy.startedTitle, {
        variant: 'info',
        subTitle: buildStartedSubtitle(response?.data?.message, copy.gerund, echoed)
      })
      onClose()
    },
    onError: (error: any) => {
      if (error?.status === HttpStatusCode.GATEWAY_TIMEOUT) {
        notification('This is taking longer than expected', {
          variant: 'error',
          subTitle: 'The server may be overloaded. Please refresh the page in a few minutes and try again.'
        })
        return
      }
      notification(error?.message ?? `Unable to ${copy.verb} report`, { variant: 'error' })
    },
    meta: { silent: true }
  })

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      title={copy.modalTitle}
      maxWidth="sm"
      action={false}
    >
      <ReportActionModal
        action={action}
        targetName={targetName}
        surveyProjectTitle={surveyProjectTitle}
        termName={termName}
        isSection={isSection}
        handleCloseModal={onClose}
        onConfirm={(includeChildren) => { mutate(includeChildren) }}
        isSubmitting={isPending}
      />
    </GenericModal>
  )
}

export default ReportMutationAction
